/*
  Broadcast.js
  ------------
  Purpose: This page provides an interface for managing broadcast banners within the admin system. It allows administrators to view, create, edit, and manage broadcast banners.

  Main Logic:
  - Fetches and displays a list of broadcast banners.
  - Supports creation, editing, and management of banner fields.
  - Integrates with API endpoints for broadcast banner data operations.
  - Utilizes React hooks for state management and side effects.
  - Displays success and failure modals for user feedback.
*/

/* eslint-disable no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable eqeqeq */
import { useState, useEffect } from "react";
import { Container, Row, Col, Modal, Form } from "react-bootstrap";
import { useLocation, useNavigate } from "react-router-dom";
import useCommonApi from "../hooks/common-api";
import Loader from "../components/Loader";
import SuccessModal from "../components/SuccessModal";
import FailureModal from "../components/FailureModal";
import moment from "moment";

function Broadcast() {
  const navigate = useNavigate();
  const { state } = useLocation();
  const { getBroadcastList, createOrUpdateBroadcast, deleteBroadcast } = useCommonApi();
  const [loader, setloader] = useState(false);
  const [openSuccessModal, setopenSuccessModal] = useState(undefined);
  const [openFailureModal, setopenFailureModal] = useState(undefined);
  const [successFailureMessage, setsuccessFailureMessage] = useState("");
  const removeModal = () => {
    setopenSuccessModal(undefined);
    setopenFailureModal(undefined);
  };
  const [show, setShow] = useState(false);
  const [banner, setBanner] = useState({
    broadcast_banner_id: null,
    banner_content: '',
    badge_content: '',
    action_url: '',
    action_title: '',
    status: '0',
    start_date: '',
    end_date: '',
  });
  const [bannerList, setBannerList] = useState([]);
  const [saving, setSaving] = useState(false);
  const [statusToggleId, setStatusToggleId] = useState(null);

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setBanner({ ...banner, [name]: value });
  };

  const handleClose = () => {
    setShow(false);
    setBanner({
      broadcast_banner_id: null,
      banner_content: '',
      badge_content: '',
      action_url: '',
      action_title: '',
      status: '0',
      start_date: '',
      end_date: '',
    });
  };

  const handleShow = () => setShow(true);

  async function getBannerListFn() {
    try {
      setloader(true);
      const result = await getBroadcastList();
      if (!result.error) {
        setBannerList(result.data || []);
      } else {
        setopenFailureModal('dismissible');
        setsuccessFailureMessage(result.message || 'Error fetching banners');
      }
    } catch (error) {
      console.log(error);
      setopenFailureModal('dismissible');
      setsuccessFailureMessage('Error fetching banners');
    } finally {
      setloader(false);
    }
  }

  async function handleSaveBanner(e) {
    e.preventDefault();
    try {
      setSaving(true);
      const data = {
        broadcast_banner_id: banner.broadcast_banner_id || null,
        banner_content: banner.banner_content,
        badge_content: banner.badge_content || null,
        action_url: banner.action_url,
        action_title: banner.action_title,
        status: banner.status,
        start_date: banner.start_date || null,
        end_date: banner.end_date || null,
      };

      const result = await createOrUpdateBroadcast(data);

      if (!result.error) {
        setopenSuccessModal('dismissible');
        setsuccessFailureMessage(result.message || 'Banner saved successfully');
        getBannerListFn();
        handleClose();
        setTimeout(() => {
          removeModal();
        }, 3000);
      } else {
        setopenFailureModal('dismissible');
        setsuccessFailureMessage(result.message || 'Error saving banner');
      }
    } catch (error) {
      console.log(error);
      setopenFailureModal('dismissible');
      setsuccessFailureMessage('Error saving banner');
    } finally {
      setSaving(false);
    }
  }

  async function handleDeleteBanner(bannerId) {
    if (!window.confirm('Are you sure you want to delete this banner?')) {
      return;
    }
    try {
      setloader(true);
      const result = await deleteBroadcast(bannerId);
      if (!result.error) {
        setopenSuccessModal('dismissible');
        setsuccessFailureMessage(result.message || 'Banner deleted successfully');
        getBannerListFn();
        setTimeout(() => {
          removeModal();
        }, 3000);
      } else {
        setopenFailureModal('dismissible');
        setsuccessFailureMessage(result.message || 'Error deleting banner');
      }
    } catch (error) {
      console.log(error);
      setopenFailureModal('dismissible');
      setsuccessFailureMessage('Error deleting banner');
    } finally {
      setloader(false);
    }
  }

  async function handleToggleStatus(item) {
    try {
      const newStatus = item?.status === '1' ? '0' : '1';
      setStatusToggleId(item?.broadcast_banner_id);

      const payload = {
        broadcast_banner_id: item?.broadcast_banner_id,
        banner_content: item?.banner_content || '',
        badge_content: item?.badge_content || null,
        action_url: item?.action_url || '',
        action_title: item?.action_title || '',
        status: newStatus,
        start_date: item?.start_date
          ? moment(item.start_date).format('YYYY-MM-DD')
          : null,
        end_date: item?.end_date
          ? moment(item.end_date).format('YYYY-MM-DD')
          : null,
      };

      const result = await createOrUpdateBroadcast(payload);

      if (!result.error) {
        setopenSuccessModal('dismissible');
        setsuccessFailureMessage(
          result.message || 'Banner status updated successfully'
        );
        getBannerListFn();
        setTimeout(() => {
          removeModal();
        }, 2000);
      } else {
        setopenFailureModal('dismissible');
        setsuccessFailureMessage(
          result.message || 'Error updating banner status'
        );
      }
    } catch (error) {
      console.log(error);
      setopenFailureModal('dismissible');
      setsuccessFailureMessage('Error updating banner status');
    } finally {
      setStatusToggleId(null);
    }
  }

  useEffect(() => {
    getBannerListFn();
  }, []);

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return moment(dateString).format('DD MMM YYYY');
  };

  return (
    <>
      <Container fluid>
        <Row>
          <Col sm={12}>
            <h2 className="fw-700 fs-30 pt-4">Broadcast Banners</h2>
            <div className="d-flex py-4">
              <div className="ms-auto">
                <button
                  type="button"
                  className="grd-btn rounded-pill py-2 d-flex align-items-center"
                  onClick={() => handleShow()}
                >
                  Add Banner
                  <svg className="icon fs-20 ms-1">
                    <use href="#icon_rightarrow"></use>
                  </svg>
                </button>
              </div>
            </div>
          </Col>
        </Row>
        <Row>
          <Col sm={12} className="normal-tbl">
            <table>
              <tr className="tr-bg">
                <th>Badge Content</th>
                <th>Banner Content</th>
                <th>Action URL</th>
                <th>Action Title</th>
                <th>Status</th>
                <th>Start Date</th>
                <th>End Date</th>
                <th>Created Date</th>
                <th>Actions</th>
              </tr>
              {loader && (
                <tr>
                  <td colSpan={9} className="text-center">
                    <Loader />
                  </td>
                </tr>
              )}
              {!loader && bannerList.length === 0 && (
                <tr>
                  <td colSpan={9} className="text-center">
                    No banners found
                  </td>
                </tr>
              )}
              {bannerList?.map((item, index) => (
                <tr key={item?.broadcast_banner_id || index}>
                  <td>
                    <div style={{ maxWidth: '150px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {item?.badge_content || '-'}
                    </div>
                  </td>
                  <td>
                    <div style={{ maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {item?.banner_content}
                    </div>
                  </td>
                  <td>
                    <a href={item?.action_url} target="_blank" rel="noreferrer" style={{ maxWidth: '200px', display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {item?.action_url}
                    </a>
                  </td>
                  <td>{item?.action_title}</td>
                  <td>
                    <div className="form-check form-switch">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        checked={item?.status === '1'}
                        disabled={
                          saving ||
                          statusToggleId === item?.broadcast_banner_id
                        }
                        onChange={() => handleToggleStatus(item)}
                      />
                      <label className="form-check-label">
                        {item?.status === '1' ? 'Active' : 'Inactive'}
                      </label>
                    </div>
                  </td>
                  <td>{formatDate(item?.start_date)}</td>
                  <td>{formatDate(item?.end_date)}</td>
                  <td>{formatDate(item?.createdAt)}</td>
                  <td>
                    <div className="d-flex align-items-center">
                      <button
                        type="button"
                        className="arrow-btn me-2"
                        onClick={() => {
                          setBanner({
                            broadcast_banner_id: item?.broadcast_banner_id,
                            banner_content: item?.banner_content || '',
                            badge_content: item?.badge_content || '',
                            action_url: item?.action_url || '',
                            action_title: item?.action_title || '',
                            status: item?.status || '0',
                            start_date: item?.start_date ? moment(item.start_date).format('YYYY-MM-DD') : '',
                            end_date: item?.end_date ? moment(item.end_date).format('YYYY-MM-DD') : '',
                          });
                          handleShow();
                        }}
                      >
                        <svg className="icon fs-24">
                          <use href="#icon_edit"></use>
                        </svg>
                      </button>
                      <button
                        type="button"
                        className="arrow-btn"
                        onClick={() => handleDeleteBanner(item?.broadcast_banner_id)}
                      >
                        <svg className="icon fs-24">
                          <use href="#icon_delete"></use>
                        </svg>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </table>
          </Col>
        </Row>
      </Container>
      {/* Modal  */}
      <Modal
        size="lg"
        contentClassName="rounded-4"
        show={show}
        onHide={handleClose}
        centered
        animation={true}
      >
        <Modal.Header className="border-1" closeButton>
          <Modal.Title className="fw-700 fs-30 mx-4">
            {banner?.broadcast_banner_id ? 'Edit' : 'Add'} Broadcast Banner
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="pt-0">
          <Form onSubmit={handleSaveBanner} className="mx-4 mt-4">
            <Form.Group className="mb-3" controlId="badgeContent">
              <Form.Label>Badge Content</Form.Label>
              <Form.Control
                className="rounded-pill1"
                type="text"
                placeholder="Enter badge content (optional)"
                name="badge_content"
                value={banner.badge_content}
                onChange={handleInputChange}
              />
              <Form.Text className="text-muted">
                Short text that appears as a badge on the banner (e.g., "New", "Sale", "Limited Time").
              </Form.Text>
            </Form.Group>
            <Form.Group className="mb-3" controlId="bannerContent">
              <Form.Label>Banner Content</Form.Label>
              <Form.Control
                className="rounded-pill1"
                as="textarea"
                rows={3}
                placeholder="Enter banner content"
                required
                name="banner_content"
                value={banner.banner_content}
                onChange={handleInputChange}
              />
            </Form.Group>
            <Row>
              <Col>
                <Form.Group className="mb-3" controlId="actionUrl">
                  <Form.Label>Action URL</Form.Label>
                  <Form.Control
                    className="rounded-pill1"
                    type="url"
                    placeholder="Enter action URL"
                    required
                    name="action_url"
                    value={banner.action_url}
                    onChange={handleInputChange}
                  />
                </Form.Group>
              </Col>
              <Col>
                <Form.Group className="mb-3" controlId="actionTitle">
                  <Form.Label>Action Title</Form.Label>
                  <Form.Control
                    className="rounded-pill1"
                    type="text"
                    placeholder="Enter action title"
                    required
                    name="action_title"
                    value={banner.action_title}
                    onChange={handleInputChange}
                  />
                </Form.Group>
              </Col>
            </Row>
            <Form.Group className="mb-3" controlId="status">
              <Form.Label>Status</Form.Label>
              <Form.Select
                className="rounded-pill1"
                name="status"
                value={banner.status}
                onChange={handleInputChange}
                required
              >
                <option value="0">Inactive</option>
                <option value="1">Active</option>
              </Form.Select>
              <Form.Text className="text-muted">
                Note: Only one banner can be active at a time. Activating a banner will automatically deactivate others.
              </Form.Text>
            </Form.Group>
            <Row>
              <Col>
                <Form.Group className="mb-3" controlId="startDate">
                  <Form.Label>Start Date (Optional)</Form.Label>
                  <Form.Control
                    className="rounded-pill1"
                    type="date"
                    name="start_date"
                    value={banner.start_date}
                    onChange={handleInputChange}
                  />
                </Form.Group>
              </Col>
              <Col>
                <Form.Group className="mb-3" controlId="endDate">
                  <Form.Label>End Date (Optional)</Form.Label>
                  <Form.Control
                    className="rounded-pill1"
                    type="date"
                    name="end_date"
                    value={banner.end_date}
                    onChange={handleInputChange}
                  />
                  <Form.Text className="text-muted">
                    Banner will automatically become inactive after the end date.
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>
            <button disabled={saving} type="submit" className="grd-btn mt-2 rounded-pill w-100">
              {saving ? <Loader /> : 'Save'}
            </button>
          </Form>
        </Modal.Body>
      </Modal>
      <SuccessModal
        open={openSuccessModal}
        removeModal={removeModal}
        successFailureMessage={successFailureMessage}
      />
      <FailureModal
        open={openFailureModal}
        removeModal={removeModal}
        successFailureMessage={successFailureMessage}
      />
    </>
  );
}

export default Broadcast;
